import React, { useEffect, useState } from 'react';

import { InboxOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Upload,
  message,
} from 'antd';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import { useTranslation } from 'react-i18next';

import { DiClient } from 'models/DiClient';

import appI18n from '../../../../config/i18n';

const { Dragger } = Upload;

// Tamaño máximo de archivo: 20 MB
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB en bytes

interface UploadFileModalProps {
  isOpen: boolean;
  handleModalCancel: () => void;
  submit: (clientId: number, description: string, file: File) => void;
  clients: DiClient[];
}

function UploadFileModal({
  isOpen,
  handleModalCancel,
  submit,
  clients,
}: UploadFileModalProps) {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const { t } = useTranslation(undefined, { i18n: appI18n });

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      setFileList([]);
    }
  }, [isOpen, form]);

  const onFinish = async (values: any) => {
    if (fileList.length === 0) {
      messageApi.error(
        t(
          'scenes.data_ingester.upload_files_history.components.upload_file_modal.errors.must_upload_file'
        )
      );
      return;
    }

    setLoading(true);

    try {
      const file = fileList[0] as any;
      if (!file) {
        messageApi.error(
          t(
            'scenes.data_ingester.upload_files_history.components.upload_file_modal.errors.cant_access_file'
          )
        );
        setLoading(false);
        return;
      }

      // Enviar el archivo
      await submit(values.clientId, values.description || '', file);
    } catch (error) {
      console.error('Error al enviar el archivo:', error);
      messageApi.error(
        t(
          'scenes.data_ingester.upload_files_history.components.upload_file_modal.errors.upload_failed'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const props: UploadProps = {
    onRemove: () => {
      setFileList([]);
    },
    beforeUpload: (file) => {
      // Verificar el tipo de archivo
      const isExcelOrCSV =
        file.type ===
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel' ||
        file.type === 'text/csv' ||
        file.name.endsWith('.xlsx') ||
        file.name.endsWith('.xls') ||
        file.name.endsWith('.csv');

      if (!isExcelOrCSV) {
        messageApi.error(
          t(
            'scenes.data_ingester.upload_files_history.components.upload_file_modal.errors.invalid_file_type'
          )
        );
        return Upload.LIST_IGNORE;
      }

      // Verificar el tamaño del archivo
      if (file.size > MAX_FILE_SIZE) {
        messageApi.error(
          t(
            'scenes.data_ingester.upload_files_history.components.upload_file_modal.errors.file_too_large'
          )
        );
        return Upload.LIST_IGNORE;
      }

      console.log('Archivo seleccionado:', file.name, file.type, file.size);
      setFileList([file]);
      return false;
    },
    fileList,
    maxCount: 1,
  };

  return (
    <>
      {contextHolder}

      <Modal
        title={t(
          'scenes.data_ingester.upload_files_history.components.upload_file_modal.title'
        )}
        open={isOpen}
        onCancel={handleModalCancel}
        maskClosable={false}
        destroyOnClose
        footer={null}
        width={500}
      >
        <Divider style={{ marginTop: '15px', marginBottom: '15px' }} />

        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="clientId"
                label={t(
                  'scenes.data_ingester.upload_files_history.components.upload_file_modal.labels.carrier'
                )}
                rules={[
                  {
                    required: true,
                    message: t(
                      'scenes.data_ingester.upload_files_history.components.upload_file_modal.validations.carrier.required'
                    ),
                  },
                ]}
              >
                <Select
                  placeholder={t(
                    'scenes.data_ingester.upload_files_history.components.upload_file_modal.placeholders.select_carrier'
                  )}
                  disabled={loading}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  filterSort={(optionA, optionB) =>
                    (optionA?.children as unknown as string)
                      .toLowerCase()
                      .localeCompare(
                        (optionB?.children as unknown as string).toLowerCase()
                      )
                  }
                >
                  {clients.map((client) => (
                    <Select.Option key={client.id} value={client.id}>
                      {client.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="description"
                label={t(
                  'scenes.data_ingester.upload_files_history.components.upload_file_modal.labels.description'
                )}
              >
                <Input.TextArea
                  placeholder={t(
                    'scenes.data_ingester.upload_files_history.components.upload_file_modal.placeholders.description_optional'
                  )}
                  disabled={loading}
                  rows={3}
                  maxLength={500}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} lg={24}>
              <Form.Item
                name="file"
                label={t(
                  'scenes.data_ingester.upload_files_history.components.upload_file_modal.labels.file'
                )}
                required
              >
                <Dragger
                  {...props}
                  disabled={loading}
                  accept=".csv,.xls,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    {t(
                      'scenes.data_ingester.upload_files_history.components.upload_file_modal.upload.drag_text'
                    )}
                  </p>
                  <p className="ant-upload-hint">
                    {t(
                      'scenes.data_ingester.upload_files_history.components.upload_file_modal.upload.hint'
                    )}
                  </p>
                </Dragger>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24}>
              <Button
                onClick={() => {
                  form.submit();
                }}
                loading={loading}
                type="primary"
                block
              >
                {t(
                  'scenes.data_ingester.upload_files_history.components.upload_file_modal.buttons.upload'
                )}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}

export default UploadFileModal;
